import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";

import { useDispatch } from "react-redux";
import { setCurrentPageName } from "../../redux/pageSlice";
import {
  getSidebarOrderAPI,
  updateSidebarOrderAPI,
} from "../../api/rbac";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import "../LayoutCss/sidebar.css";

import VelaLogo from "../../assets/Logo/project_icon.svg";
import VelaKoda from "../../assets/Logo/project_logo.svg";

const APP_VERSION = process.env.REACT_APP_VERSION || "1.0.1";

const Sidebar = () => {
  const location = useLocation();

  const dispatch = useDispatch();

  const { pages, user, roles } = useAuth();

  const [expanded, setExpanded] = useState(true);
  const [orderedPages, setOrderedPages] = useState([]);
  const [orderLoaded, setOrderLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  const toggleSidebar = () => setExpanded(!expanded);

  /**
   * =============================
   * Get current role
   * =============================
   */

  const currentRole = user?.is_super_admin
    ? "superadmin"
    : roles?.[0]?.toLowerCase();

  /**
   * =============================
   * Filter allowed pages
   * =============================
   */

  const allowedPages = user?.is_super_admin
    ? pages
    : pages?.filter((page) => page.can_view);

  /**
   * =============================
   * Static Pages
   * =============================
   */

  const staticMenus = [
    {
      name: "Dashboard",

      icon: "columns-gap",

      route: `/${currentRole}/dashboard`,

      show: true,
    },

    {
      name: "RBAC",

      icon: "shield-lock",

      route: "/superadmin/rbac",

      show:
        user?.is_super_admin ||
        allowedPages?.some((p) => p.page_route === "/superadmin/rbac"),
    },
  ];

  /**
   * =============================
   * Drag & Drop Ordering (Dynamic Pages)
   * =============================
   */

  useEffect(() => {
    let active = true;

    const loadSidebarOrder = async () => {
      if (active) {
        setOrderLoaded(false);
      }

      if (!allowedPages?.length) {
        if (active) {
          setOrderedPages([]);
          setOrderLoaded(true);
        }
        return;
      }

      try {
        const response = await getSidebarOrderAPI();
        const serverOrder = response?.data?.data?.page_ids || [];
        const normalizedOrder = Array.isArray(serverOrder)
          ? serverOrder.map((id) => String(id))
          : [];

        if (!Array.isArray(serverOrder) || serverOrder.length === 0) {
          if (active) {
            setOrderedPages(allowedPages);
            setOrderLoaded(true);
          }
          return;
        }

        const byId = new Map(
          allowedPages.map((page) => [String(page.page_id), page]),
        );
        const next = [];

        normalizedOrder.forEach((id) => {
          const page = byId.get(String(id));
          if (page) {
            next.push(page);
          }
        });

        const missing = allowedPages.filter(
          (page) => !normalizedOrder.includes(String(page.page_id)),
        );

        if (active) {
          setOrderedPages([...next, ...missing]);
          setOrderLoaded(true);
        }
      } catch (error) {
        if (active) {
          setOrderedPages(allowedPages);
          setOrderLoaded(true);
        }
      }
    };

    loadSidebarOrder();

    return () => {
      active = false;
    };
  }, [allowedPages]);

  const orderedIds = useMemo(
    () => orderedPages.map((page) => String(page.page_id)),
    [orderedPages],
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setIsDragging(false);
    if (!over || active.id === over.id) return;

    const fromIndex = orderedPages.findIndex(
      (page) => String(page.page_id) === String(active.id),
    );
    const toIndex = orderedPages.findIndex(
      (page) => String(page.page_id) === String(over.id),
    );
    if (fromIndex === -1 || toIndex === -1) return;

    const next = arrayMove(orderedPages, fromIndex, toIndex);
    setOrderedPages(next);
    if (orderLoaded) {
      updateSidebarOrderAPI(next.map((page) => page.page_id)).catch((error) =>
        console.warn("Failed to save sidebar order", error),
      );
    }
  };

  const handleLinkClick = (event, pageName) => {
    if (isDragging) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    handleClick(pageName);
  };

  /**
   * =============================
   * Click handler
   * =============================
   */

  const handleClick = (pageName) => {
    dispatch(setCurrentPageName(pageName));
  };

  return (
    <div
      className={`sidebar d-flex flex-column ${
        expanded ? "expanded" : "collapsed"
      }`}
    >
      {/* ========================= */}
      {/* LOGO */}
      {/* ========================= */}

      <div
        className={`logo-section ${
          expanded
            ? "d-flex justify-content-between align-items-center pe-3 py-2"
            : "d-flex justify-content-center align-items-center py-2"
        }`}
      >
        <img
          src={expanded ? VelaKoda : VelaLogo}
          alt="Logo"
          style={{
            width: expanded ? "160px" : "40px",
            height: expanded ? "30px" : "40px",
            objectFit: "contain",
          }}
        />

        <button onClick={toggleSidebar} className="sidebar-toggler">
          <i
            className={`bi ${
              expanded ? "bi-chevron-double-left" : "bi-chevron-double-right"
            }`}
          />
        </button>
      </div>

      {/* ========================= */}
      {/* STATIC MENU */}
      {/* ========================= */}

      <ul className="sidebar-menu">
        {staticMenus
          .filter((menu) => menu.show)
          .map((menu) => {
            const isActive = location.pathname === menu.route;

            return (
              <li key={menu.name} className={isActive ? "active" : ""}>
                <Link to={menu.route} onClick={() => handleClick(menu.name)}>
                  <i className={`bi bi-${menu.icon}`} />

                  {expanded && <span>{menu.name}</span>}
                </Link>
              </li>
            );
          })}
      </ul>

      {/* ========================= */}
      {/* DYNAMIC RBAC MENU */}
      {/* ========================= */}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setIsDragging(false)}
      >
        <SortableContext
          items={orderedIds}
          strategy={verticalListSortingStrategy}
        >
          <ul className={`sidebar-menu ${isDragging ? "is-dragging" : ""}`}>
            {orderedPages?.map((page) => {
              const route = `/${currentRole}/page/${page.model_name}`;
              const isActive = location.pathname === route;

              return (
                <SortableMenuItem
                  key={page.page_id}
                  id={String(page.page_id)}
                  isActive={isActive}
                >
                  <Link
                    to={route}
                    onClick={(event) => handleLinkClick(event, page.page_name)}
                  >
                    <i className={`bi bi-${page.page_icon || "grid"}`} />

                    {expanded && <span>{page.page_name}</span>}
                  </Link>
                </SortableMenuItem>
              );
            })}
          </ul>
        </SortableContext>
      </DndContext>
      {/*  sidebar version */}
      <div className="sidebar-version">
        {expanded ? <span>{`Version ${APP_VERSION}`}</span> : <span>{`v${APP_VERSION}`}</span>}
      </div>
    </div>
  );
};

const SortableMenuItem = ({ id, isActive, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`sidebar-draggable ${isActive ? "active" : ""} ${
        isDragging ? "dragging" : ""
      } ${isOver ? "drag-over" : ""}`}
      {...attributes}
      {...listeners}
    >
      {children}
    </li>
  );
};

export default Sidebar;
